import type { EnrichedOpportunity, NewsSentimentType, TradeSignal } from '@/models/marketData';

export type RsiFilter = 'all' | 'momentum' | 'overheated' | 'below-55';
export type AdxFilter = 'all' | 'strong' | 'weak';
export type VolumeFilter = 'all' | 'surge' | 'normal';
export type BooleanFilter = 'all' | 'yes' | 'no';
export type MacdFilter = 'all' | 'bullish' | 'below-signal' | 'fresh';

export interface OpportunityFilterState {
  globalSearch: string;
  companySearch: string;
  signal: TradeSignal | 'all';
  minScore: number;
  rsi: RsiFilter;
  adx: AdxFilter;
  volume: VolumeFilter;
  superTrend: BooleanFilter;
  emaAlignment: BooleanFilter;
  macd: MacdFilter;
  newsSentiment: NewsSentimentType | 'all';
  topTen: boolean;
}

export const initialOpportunityFilters: OpportunityFilterState = {
  globalSearch: '',
  companySearch: '',
  signal: 'all',
  minScore: 0,
  rsi: 'all',
  adx: 'all',
  volume: 'all',
  superTrend: 'all',
  emaAlignment: 'all',
  macd: 'all',
  newsSentiment: 'all',
  topTen: false,
};

const matchesBoolean = (filter: BooleanFilter, value: boolean): boolean =>
  filter === 'all' || (filter === 'yes' ? value : !value);

export const filterOpportunities = (
  opportunities: EnrichedOpportunity[],
  filters: OpportunityFilterState,
): EnrichedOpportunity[] => {
  const globalQuery = filters.globalSearch.trim().toLowerCase();
  const companyQuery = filters.companySearch.trim().toLowerCase();
  const filtered = opportunities.filter((item) => {
    const { tradeInstrument, indicators, metrics } = item;
    const globalHaystack = globalQuery
      ? [
          tradeInstrument.symbol,
          tradeInstrument.name,
          tradeInstrument.instrumentKey,
          tradeInstrument.exchange,
          ...item.reasons,
          ...item.relatedNews.map(({ heading }) => heading),
        ]
          .join(' ')
          .toLowerCase()
      : '';
    const companyHaystack = companyQuery
      ? `${tradeInstrument.symbol} ${tradeInstrument.name}`.toLowerCase()
      : '';

    if (globalQuery && !globalHaystack.includes(globalQuery)) return false;
    if (companyQuery && !companyHaystack.includes(companyQuery)) return false;
    if (filters.signal !== 'all' && item.signal !== filters.signal) return false;
    if (item.score < filters.minScore) return false;
    if (filters.rsi === 'momentum' && !(indicators.rsi14 >= 55 && indicators.rsi14 <= 70)) {
      return false;
    }
    if (filters.rsi === 'overheated' && indicators.rsi14 < 70) return false;
    if (filters.rsi === 'below-55' && indicators.rsi14 >= 55) return false;
    if (filters.adx === 'strong' && indicators.adx14 < 25) return false;
    if (filters.adx === 'weak' && indicators.adx14 >= 25) return false;
    if (filters.volume === 'surge' && indicators.volumeRatio20 < 1.5) return false;
    if (filters.volume === 'normal' && indicators.volumeRatio20 >= 1.5) return false;
    if (!matchesBoolean(filters.superTrend, indicators.superTrendBullish)) return false;
    if (!matchesBoolean(filters.emaAlignment, metrics.emaAligned)) return false;
    if (filters.macd === 'bullish' && !metrics.macdBullish) return false;
    if (filters.macd === 'below-signal' && metrics.macdBullish) return false;
    if (filters.macd === 'fresh' && !metrics.freshMacdCrossover) return false;
    if (filters.newsSentiment !== 'all' && metrics.newsSentiment.type !== filters.newsSentiment) {
      return false;
    }
    return true;
  });

  if (!filters.topTen) return filtered;
  return [...filtered]
    .sort((a, b) => b.score - a.score || b.confidence - a.confidence)
    .slice(0, 10);
};

export const boardFilters: Record<string, Partial<OpportunityFilterState>> = {
  'Momentum stocks': {},
  'Momentum ready': { adx: 'strong', rsi: 'momentum' },
  'Volume surge': { volume: 'surge' },
  'Fresh MACD': { macd: 'fresh' },
  'Overheated RSI': { rsi: 'overheated' },
  'Constructive setups': { signal: 5 },
  'EMA alignment': { emaAlignment: 'yes' },
  'Bullish SuperTrend': { superTrend: 'yes' },
};

export interface BoardNavigationProps {
  activeBoard: string | null;
  onNavigate: (label: string) => void;
}
